package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.Giver
import com.dotdrives.model.NewGiver
import com.dotdrives.repository.GiverRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

data class NewGiverUiState(
    val editingGiverId: String? = null,
    val giverType: String = "individual",
    val firstName: String = "",
    val lastName: String = "",
    val displayName: String = "",
    val spouseName: String = "",
    val email: String = "",
    val phone: String = "",
    val address: String = "",
    val stewardUserId: String? = null,
    val tags: List<String> = emptyList(),
    val saving: Boolean = false,
    val savedGiver: Giver? = null,
    val createDriveAfterSave: Boolean = false,
    val error: String? = null,
)

class NewGiverController(
    private val repo: GiverRepository,
    scope: CoroutineScope,
    private val orgId: String,
    private val defaultStewardUserId: String? = null,
) : DotController<NewGiverUiState>(
    NewGiverUiState(stewardUserId = defaultStewardUserId),
    scope,
) {
    private var editingGiver: Giver? = null
    private var generation = 0

    fun startCreate() {
        generation++
        editingGiver = null
        update { NewGiverUiState(stewardUserId = defaultStewardUserId) }
    }

    fun startEdit(giver: Giver) {
        if (giver.orgId != orgId) return
        generation++
        editingGiver = giver
        update {
            NewGiverUiState(
                editingGiverId = giver.id,
                giverType = giver.giverType,
                firstName = giver.firstName.orEmpty(),
                lastName = giver.lastName.orEmpty(),
                displayName = giver.displayName,
                spouseName = giver.spouseName.orEmpty(),
                email = giver.email.orEmpty(),
                phone = giver.phone.orEmpty(),
                address = giver.address.orEmpty(),
                stewardUserId = giver.stewardUserId,
                tags = giver.tags,
            )
        }
    }

    fun setGiverType(value: String) {
        if (value !in GIVER_TYPES) return
        update { it.copy(giverType = value, error = null) }
    }

    fun setFirstName(value: String) {
        update { it.copy(firstName = value, error = null) }
    }

    fun setLastName(value: String) {
        update { it.copy(lastName = value, error = null) }
    }

    fun setDisplayName(value: String) {
        update { it.copy(displayName = value, error = null) }
    }

    fun setSpouseName(value: String) {
        update { it.copy(spouseName = value, error = null) }
    }

    fun setEmail(value: String) {
        update { it.copy(email = value, error = null) }
    }

    fun setPhone(value: String) {
        update { it.copy(phone = value, error = null) }
    }

    fun setAddress(value: String) {
        update { it.copy(address = value, error = null) }
    }

    fun setStewardUserId(value: String?) {
        update { it.copy(stewardUserId = value, error = null) }
    }

    fun setTags(value: List<String>) {
        update { it.copy(tags = value, error = null) }
    }

    fun save() = saveWithIntent(false)

    fun saveAndNewDrive() = saveWithIntent(true)

    private fun saveWithIntent(createDrive: Boolean) {
        val current = state.value
        if (current.saving) return
        val firstName = current.firstName.trim()
        val lastName = current.lastName.trim()
        val displayName = if (current.giverType == INDIVIDUAL_TYPE) {
            listOf(firstName, lastName).filter(String::isNotEmpty).joinToString(" ")
        } else {
            current.displayName.trim()
        }
        if (displayName.isBlank()) {
            update { it.copy(error = "Display name is required") }
            return
        }
        val email = current.email.trim().takeIf(String::isNotEmpty)
        if (email != null && !email.isValidEmail()) {
            update { it.copy(error = "Enter a valid email address") }
            return
        }
        val giver = NewGiver(
            giverType = current.giverType,
            firstName = firstName,
            lastName = lastName,
            displayName = displayName,
            spouseName = current.spouseName.trim().takeIf(String::isNotEmpty),
            email = email,
            phone = current.phone.trim().takeIf(String::isNotEmpty),
            address = current.address.trim().takeIf(String::isNotEmpty),
            stewardUserId = current.stewardUserId,
            tags = current.tags.map(String::trim).filter(String::isNotEmpty).distinct(),
        )
        update { it.copy(saving = true, error = null, savedGiver = null, createDriveAfterSave = createDrive) }
        val request = generation
        val original = editingGiver
        scope.launch {
            try {
                val result = if (original == null) {
                    repo.create(orgId, giver)
                } else {
                    repo.update(
                        orgId = orgId,
                        giver = original.copy(
                            giverType = giver.giverType,
                            firstName = giver.firstName.takeIf(String::isNotEmpty),
                            lastName = giver.lastName.takeIf(String::isNotEmpty),
                            displayName = giver.displayName,
                            spouseName = giver.spouseName,
                            email = giver.email,
                            phone = giver.phone,
                            address = giver.address,
                            stewardUserId = giver.stewardUserId,
                            tags = giver.tags,
                        ),
                    )
                }
                result
                    .onSuccess { saved ->
                        if (request != generation) return@onSuccess
                        update {
                            it.copy(
                                saving = false,
                                savedGiver = saved,
                                error = null,
                            )
                        }
                    }
                    .onFailure { if (request == generation) publishError(it) }
            } catch (error: Throwable) {
                if (request == generation) publishError(error)
            }
        }
    }

    private fun publishError(error: Throwable) {
        if (error is CancellationException) throw error
        update {
            it.copy(
                saving = false,
                error = error.message ?: "Something went wrong",
            )
        }
    }
}

private val GIVER_TYPES = setOf("individual", "organization")
private const val INDIVIDUAL_TYPE = "individual"

private fun String.isValidEmail(): Boolean {
    val atIndex = indexOf('@')
    return atIndex > 0 &&
        atIndex == lastIndexOf('@') &&
        atIndex < lastIndex &&
        none(Char::isWhitespace)
}
